import React, { useState, useEffect, useMemo } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus } from '@fortawesome/free-solid-svg-icons';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import CommonTable from '../../../common/table/ComonTable';
import PageHeadingSection from '../../../common/pageheading/PageHeadingSection';
import apiServices from '../../../../services/apiService';
import SearchBar from '../../../common/formfields/SearchBar';
import DeleteModal from '../../../common/commonmodal/DeleteModal';
import Breadcrumbs from '../../../common/BreadCrum/BreadCrum';

const AlertNotificationList = () => {
    const navigate = useNavigate();
    const [currentPage, setCurrentPage] = useState(1);
    const [alerts, setAlerts] = useState([]);
    const [loading, setLoading] = useState(false);
    const [searchQuery, setSearchQuery] = useState("");
    const [statusFilter, setStatusFilter] = useState("");
    const [pageInfo, setPageInfo] = useState({
        pageNumber: 1,
        pageSize: 10,
        totalPages: 1,
        totalRecords: 0
    });

    const [isDeleteOpen, setIsDeleteOpen] = useState(false);
    const [recordToDelete, setRecordToDelete] = useState(null);

    const fetchAlerts = async () => {
        setLoading(true);
        try {
            const response = await apiServices({
                method: "GET",
                url: `/RaisedAlert/GetAllRaisedAlert`
            });

            if (response.isSuccess || response.success || response.IsSuccess) {
                let data = [];
                if (Array.isArray(response.data)) {
                    data = response.data;
                }
                setAlerts(data);
                setPageInfo(prev => ({
                    ...prev,
                    totalRecords: data.length,
                    totalPages: Math.ceil(data.length / prev.pageSize)
                }));
            }
        } catch (error) {
            console.error("Error fetching alerts:", error);
            toast.error("Failed to fetch notifications.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchAlerts();
    }, []);

    const handleLimitChange = (newLimit) => {
        setPageInfo(prev => ({ ...prev, pageSize: newLimit, pageNumber: 1 }));
        setCurrentPage(1);
    };

    const tableHeaders = [
        { label: "NO", key: "no" },
        { label: "IMAGE", key: "AlertImage" },
        { label: "TITLE", key: "AlertTypeName" },
        { label: "DESCRIPTION", key: "AlertDescription" },
        { label: "STATUS", key: "IsActive" },
        { label: "ACTIONS", key: "actions" }
    ];

    const renderCell = (key, row, index) => {
        const val = (k) => row[k] || row[k.charAt(0).toLowerCase() + k.slice(1)] || "—";
        switch (key) {
            case "no":
                return <span className="text-xs font-semibold text-[#1E293B]">{index + 1 + (currentPage - 1) * pageInfo.pageSize}</span>;
            case "AlertImage":
                const imageSrc = val(key);
                if (imageSrc && imageSrc !== "—") {
                    return (
                        <div className="w-12 h-12 rounded-md overflow-hidden bg-gray-100 flex items-center justify-center">
                            <img src={imageSrc} alt="Preview" className="w-full h-full object-cover" />
                        </div>
                    );
                }
                return <span className="text-xs text-gray-400">No Image</span>;
            case "AlertTypeName":
            case "AlertDescription":
                return <span className="text-xs font-semibold text-[#1E293B]">{val(key)}</span>;
            case "IsActive":
                const status = row.IsActive || row.isActive || 'Active';
                let statusColor = "bg-gray-100 text-gray-600";
                if (status === 'Active') statusColor = "bg-[#DCFCE7] text-[#166534]";
                if (status === 'Inactive') statusColor = "bg-[#FEF9C3] text-[#D97706]";
                return (
                    <span className={`px-3 py-1 rounded-full text-[10px] font-black shadow-sm uppercase ${statusColor}`}>
                        {status}
                    </span>
                );
            case "actions":
                return null;
            default:
                return <span className="text-xs font-normal text-[#64748B]">{row[key] || "—"}</span>;
        }
    };

    const handleDeleteClick = (row) => {
        setRecordToDelete(row);
        setIsDeleteOpen(true);
    };

    const confirmDelete = async () => {
        if (!recordToDelete) return;
        try {
            const id = recordToDelete.AlertRaisedId || recordToDelete.alertRaisedId || recordToDelete.Id || recordToDelete.id;
            const response = await apiServices({
                method: "DELETE",
                url: `/RaisedAlert/RemoveExistingAlertTypeMaster/${id}`
            });
            if (response.success || response.IsSuccess || response.isSuccess) {
                toast.success("Notification deleted successfully.");
                fetchAlerts();
            } else {
                toast.error(response.message || "Failed to delete notification.");
            }
        } catch (error) {
            console.error("Delete error:", error);
            toast.error("An error occurred during deletion.");
        } finally {
            setIsDeleteOpen(false);
            setRecordToDelete(null);
        }
    };

    const filteredData = useMemo(() => {
        let data = alerts;
        if (statusFilter) {
            data = data.filter(item => (item.IsActive || item.isActive) === statusFilter);
        }
        if (searchQuery) {
            const query = searchQuery.toLowerCase();
            data = data.filter(item =>
                (item.AlertTypeName || item.alertTypeName || "").toLowerCase().includes(query) ||
                (item.AlertDescription || item.alertDescription || "").toLowerCase().includes(query)
            );
        }
        return data;
    }, [alerts, statusFilter, searchQuery]);

    const paginatedData = useMemo(() => {
        const start = (currentPage - 1) * pageInfo.pageSize;
        return filteredData.slice(start, start + pageInfo.pageSize);
    }, [filteredData, currentPage, pageInfo.pageSize]);

    const headerAction = (
        <div className="flex flex-col sm:flex-row items-center gap-3">
            <SearchBar
                placeholder="Start searching here.."
                value={searchQuery}
                onChange={(e) => {
                    setSearchQuery(e.target.value);
                    setCurrentPage(1);
                }}
                className="w-full sm:w-48"
            />
            <select
                value={statusFilter}
                onChange={(e) => {
                    setStatusFilter(e.target.value);
                    setCurrentPage(1);
                }}
                className="w-full sm:w-auto px-3 py-2 border border-gray-300 rounded-lg text-xs focus:ring-indigo-500 focus:border-indigo-500 text-gray-700 bg-white"
            >
                <option value="">All Status</option>
                <option value="Active">Active</option>
                <option value="Inactive">Inactive</option>
            </select>
        </div>
    );

    return (
        <div className="bg-dashboardbg px-6 py-4 border border-bordercolor flex flex-col rounded-lg flex-1 overflow-y-auto gap-6 pt-4">
            <Breadcrumbs />
            <PageHeadingSection
                mainHeading="Alert Notifications"
                subHeading="Manage all alert notifications"
                primaryBtnText="Add New Notification"
                primaryBtnIcon={faPlus}
                PrimaryOnClick={() => navigate('/zentcomm/alert-notification/add')}
            />

            <div className="shrink-0 flex-1 flex flex-col">
                <CommonTable
                    tableHeading="Notifications List"
                    headerAction={headerAction}
                    headers={tableHeaders}
                    data={paginatedData}
                    renderCell={(key, row) => renderCell(key, row, paginatedData.indexOf(row))}
                    pagination={{
                        ...pageInfo,
                        pageNumber: currentPage,
                        totalRecords: filteredData.length,
                        totalPages: Math.ceil(filteredData.length / pageInfo.pageSize) || 1
                    }}
                    onPageChange={setCurrentPage}
                    onLimitChange={handleLimitChange}
                    loading={loading}
                    showActionCol={true}
                    onEdit={(row) => {
                        const id = row.AlertRaisedId || row.alertRaisedId || row.Id || row.id;
                        navigate(`/zentcomm/alert-notification/edit/${id}`, { state: { id } });
                    }}
                    onDelete={handleDeleteClick}
                />
            </div>

            <DeleteModal
                isOpen={isDeleteOpen}
                onClose={() => {
                    setIsDeleteOpen(false);
                    setRecordToDelete(null);
                }}
                onConfirm={confirmDelete}
                message={`Are you sure you want to delete the notification "${recordToDelete?.AlertTypeName || recordToDelete?.alertTypeName}"?`}
            />
        </div>
    );
};

export default AlertNotificationList;
